/** Client for Hostinger PHP launch + fee APIs. */

export type LaunchRecord = {
  mint: string
  name: string
  symbol: string
  description?: string
  imageUri?: string
  website?: string
  creatorWallet: string
  twitterFeeHandle?: string | null
  twitterFeeBps?: number | null
  feeShareStatus?: 'none' | 'on_chain' | 'pending_link' | string
  feeShareWallet?: string | null
  createdAt: string
  signature?: string | null
  pumpUrl?: string
}

export type FeeLink = {
  twitterHandle: string
  wallet: string
  linkedAt?: string
}

export type FeeClaim = LaunchRecord & {
  twitterFeeHandle: string
  twitterFeeBps: number
}

export type XSession = {
  handle: string
  userId?: string
  name?: string
  avatar?: string
} | null

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text()
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error(text || `HTTP ${res.status}`)
  }
}

export async function fetchLaunches(): Promise<LaunchRecord[]> {
  const res = await fetch('/api/launches.php', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`Launches API ${res.status}`)
  const data = await readJson<{ launches?: LaunchRecord[] }>(res)
  return Array.isArray(data.launches) ? data.launches : []
}

export async function registerLaunch(launch: LaunchRecord): Promise<LaunchRecord> {
  const res = await fetch('/api/launches.php', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(launch),
  })
  const data = await readJson<{ ok?: boolean; launch?: LaunchRecord; error?: string }>(res)
  if (!res.ok || data.error) {
    throw new Error(data.error || `Register launch failed (${res.status})`)
  }
  return data.launch ?? launch
}

export async function lookupFeeLink(handle: string): Promise<FeeLink | null> {
  const h = handle.replace(/^@/, '').trim()
  if (!h) return null
  const res = await fetch(`/api/fee-links.php?handle=${encodeURIComponent(h)}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) return null
  const data = await readJson<{ link?: FeeLink | null }>(res)
  return data.link ?? null
}

export async function fetchXSession(): Promise<{
  twitterConfigured: boolean
  session: XSession
}> {
  const res = await fetch('/api/x-session.php', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) return { twitterConfigured: false, session: null }
  const data = await readJson<{
    twitterConfigured?: boolean
    session?: XSession
  }>(res)
  return {
    twitterConfigured: Boolean(data.twitterConfigured),
    session: data.session ?? null,
  }
}

export async function logoutXSession(): Promise<void> {
  await fetch('/api/x-session.php', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ action: 'logout' }),
  })
}

export async function postFeeLink(payload: {
  twitterHandle: string
  wallet: string
  message: string
  signature: string
}): Promise<FeeLink> {
  const res = await fetch('/api/fee-links.php', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = await readJson<{ ok?: boolean; link?: FeeLink; error?: string }>(res)
  if (!res.ok || data.error || !data.link) {
    throw new Error(data.error || `Fee link failed (${res.status})`)
  }
  return data.link
}

export async function fetchFeeClaims(handle?: string): Promise<{
  twitterConfigured: boolean
  handle: string | null
  linkedWallet: string | null
  claims: FeeClaim[]
}> {
  const q = handle
    ? `?handle=${encodeURIComponent(handle.replace(/^@/, ''))}`
    : ''
  const res = await fetch(`/api/fee-claims.php${q}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    return { twitterConfigured: false, handle: null, linkedWallet: null, claims: [] }
  }
  const data = await readJson<{
    twitterConfigured?: boolean
    handle?: string | null
    linkedWallet?: string | null
    claims?: FeeClaim[]
  }>(res)
  return {
    twitterConfigured: Boolean(data.twitterConfigured),
    handle: data.handle ?? null,
    linkedWallet: data.linkedWallet ?? null,
    claims: Array.isArray(data.claims) ? data.claims : [],
  }
}

export function xAuthStartUrl(): string {
  return '/api/x-auth-start.php?next=' + encodeURIComponent('/#fees')
}

export function relativeTime(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const sec = Math.round((Date.now() - t) / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 48) return `${hr}h ago`
  return `${Math.round(hr / 24)}d ago`
}

/** Encode Uint8Array as Solana-style base58. */
export function bytesToBase58(bytes: Uint8Array): string {
  const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'
  const digits = [0]
  for (const byte of bytes) {
    let carry = byte
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j]! << 8
      digits[j] = carry % 58
      carry = (carry / 58) | 0
    }
    while (carry > 0) {
      digits.push(carry % 58)
      carry = (carry / 58) | 0
    }
  }
  let zeros = 0
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++
  return '1'.repeat(zeros) + digits.reverse().map((d) => ALPHABET[d]!).join('')
}
